import React, { useState, useEffect, useRef } from "react";
import { auth } from "../firebase";
import { onAuthStateChanged, signOut } from "firebase/auth";
import type { User } from "firebase/auth";
import { useLocation, useNavigate } from "react-router-dom";
import { useLingui } from "@lingui/react";
import { t } from '@lingui/core/macro';
import { useProject } from '../contexts/ProjectContext';
import { ReactComponent as ChevronDownIcon } from "../assets/chevron-down.svg";
import { ReactComponent as QuestionMarkCircleIcon } from "../assets/question-mark-circle.svg";
import { isAdminUserEmail } from "../utils/adminAccess";
import LanguageSwitcher from "./LanguageSwitcher";
import "../App.css";

export default function NavBar(): React.ReactElement {
  // Subscribe to locale changes for t`...` macro strings rendered in this component.
  useLingui();
  const [user, setUser] = useState<User | null>(null);
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedProject, setSelectedProject } = useProject();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser: User | null) => setUser(firebaseUser));
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = async () => {
    await signOut(auth);
    navigate("/");
  };

  const handleLogin = () => navigate("/login");

  const handleChangeProject = (): void => {
    setSelectedProject(null);
  };

  const isAdmin = isAdminUserEmail(user?.email);
  const isAdminRoute = location.pathname === "/admin";

  return (
    <nav className="navbar">
      <div className="navbar-brand" aria-label={`FinTitles ${t`Beta`}`}>
        <img
          className="navbar-brand-logo"
          src="/shared/logos/Fintitles%20logo.png"
          alt=""
          aria-hidden="true"
        />
        <span className="navbar-title">FinTitles</span>
        <span className="navbar-beta-badge" aria-label={t`Beta`}>
          <span aria-hidden="true">🚧</span>
          {t`Beta`}
        </span>
      </div>
      <div className="navbar-right">
        {selectedProject && (
          <div className="navbar-project">
            <span className="project-name">{selectedProject.name}</span>
            <button onClick={handleChangeProject} className="change-project-button">
              {t`Change Project`}
            </button>
          </div>
        )}
        <a className="navbar-support" href="mailto:support@fintitles.com" target="_blank" rel="noreferrer">
          <QuestionMarkCircleIcon
            className="navbar-support-icon"
            width={16}
            height={16}
            aria-hidden="true"
            focusable="false"
          />
          {t`Email support`}
        </a>

        <LanguageSwitcher />
        <div className="navbar-user" ref={dropdownRef}>
          <button onClick={() => setOpen(!open)} className="user-button">
            {user ? user.email : t`Guest`}
            <ChevronDownIcon
              className={`dropdown-arrow ${open ? "open" : ""}`}
              width={14}
              height={14}
              style={{ marginLeft: "6px" }}
              aria-hidden="true"
              focusable="false"
            />
          </button>
          {open && (
            <div className="dropdown">
              <button className="dropdown-item" onClick={() => navigate('/feedback')}>
                {t`Feedback`}
              </button>
              {isAdmin && (
                <button
                  className={`dropdown-item ${isAdminRoute ? "active" : ""}`}
                  onClick={() => navigate('/admin')}
                >
                  {t`Admin`}
                </button>
              )}
              {user ? (
                <button onClick={handleLogout} className="dropdown-item">
                  {t`Logout`}
                </button>
              ) : (
                <button onClick={handleLogin} className="dropdown-item">
                  {t`Login / Sign Up`}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </nav>
  );
}
